/*----------------------------------
- COMPONENT: PUBLIC DOMAINS TABLE CELL
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 *
 * One resolved cell, in the archetype its column declares.
 *
 * The values come from the shared resolver the authenticated table uses, so a row reads the same
 * on both surfaces. What differs is only what a visitor can do with it.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import Icon from '@/client/components/ui/icon/Icon';
import {
    SIGNAL_DISPLAY,
    BADGE_TONE_CLASS_BY_TONE,
} from '@uniquedomains/components/contracts/signals/display';

import PublicTableHoverCard from './PublicTableHoverCard';

// Types
import type { TableCellValue } from '@uniquedomains/components/domains/table/cellValues';

/*----------------------------------
- TYPES
----------------------------------*/

type PublicDomainsTableCellProps = {
    value: TableCellValue;
    align: 'left' | 'right';
    /**
     * Whether the evidence card travels with the cell.
     *
     * The table always carries it: the card is the page's GEO content and the reason a visitor
     * trusts the figure beside it. The phone cards render client side only, behind no hover and
     * no focus of their own, so they ask for the value without twenty rows of cards nothing can
     * ever open.
     */
    hover?: boolean;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/** One verdict star, identical to the product's: the two tables render one rating, not two. */
const StarGlyph = ({ filled }: { filled: boolean }) => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill={filled ? '#e0ce3e' : 'transparent'}
        stroke={filled ? '#c9b62a' : 'rgba(27, 29, 30, 0.22)'}
        stroke-width="1.8"
        stroke-linejoin="round"
        aria-hidden="true"
    >
        <path d="m12 3 2.7 5.9 6.3.7-4.7 4.3 1.3 6.1L12 17l-5.6 3 1.3-6.1L3 9.6l6.3-.7z" />
    </svg>
);

const PublicDomainsTableCell = ({ value, align, hover = true }: PublicDomainsTableCellProps) => {
    const card = hover && value.hover ? (
        <PublicTableHoverCard
            title={value.hover.title}
            lines={value.hover.lines}
            note={value.hover.note}
            align={align === 'right' ? 'end' : 'start'}
        />
    ) : null;

    if (value.kind === 'empty') {
        return (
            <span class="ud-public-table__cell is-empty" title={value.title}>
                –{card}
            </span>
        );
    }

    if (value.kind === 'chip') {
        return (
            <span class="ud-public-table__cell is-chip" title={value.title}>
                <span
                    class="ud-public-table__status-chip"
                    style={{ color: value.colorHex, background: value.backgroundHex }}
                >
                    {value.value}
                </span>
                {card}
            </span>
        );
    }

    if (value.kind === 'stars') {
        return (
            <span
                class="ud-public-table__cell is-stars"
                title={value.title}
                role="img"
                aria-label={value.title ?? `${value.filled} out of ${value.total}`}
            >
                {Array.from({ length: value.total }, (_, index) => (
                    <StarGlyph key={index} filled={index < value.filled} />
                ))}
                {card}
            </span>
        );
    }

    if (value.kind === 'dot') {
        return (
            <span
                class="ud-public-table__cell is-dot"
                style={value.colorHex ? { color: value.colorHex } : undefined}
                title={value.title}
            >
                <span class="ud-public-table__dot" aria-hidden="true" />
                {value.value}
                {card}
            </span>
        );
    }

    if (value.kind === 'badge') {
        return (
            <span class="ud-public-table__cell is-badge" title={value.title}>
                <span class={`ud-public-table__pill is-${value.tone}`}>{value.value}</span>
                {card}
            </span>
        );
    }

    if (value.kind === 'bar') {
        return (
            <span class="ud-public-table__cell is-bar" title={value.title}>
                <span class="ud-public-table__track" aria-hidden="true">
                    <span class="ud-public-table__fill" style={{ width: `${value.percent}%` }} />
                </span>
                <span class="ud-public-table__bar-value">{value.value}</span>
                {card}
            </span>
        );
    }

    if (value.kind === 'coverage') {
        return (
            <span class="ud-public-table__cell is-coverage" title={value.title}>
                {value.items.map((item) => (
                    <span key={item.id} class="ud-public-table__coverage-item">
                        <span class="ud-public-table__dot" style={{ background: item.colorHex }} aria-hidden="true" />
                        {item.value ?? 0}
                    </span>
                ))}
                {card}
            </span>
        );
    }

    if (value.kind === 'signals') {
        return (
            <span class="ud-public-table__cell is-signals">
                {value.signals.map((signal) => {
                    const display = SIGNAL_DISPLAY[signal.type];
                    return (
                        <span
                            key={signal.type}
                            class={`ud-public-table__glyph ${BADGE_TONE_CLASS_BY_TONE[display.tone]}`}
                            title={signal.description ?? signal.label}
                        >
                            <Icon name={display.icon} size={13} strokeWidth={2} />
                        </span>
                    );
                })}
                {value.overflow.length > 0 ? (
                    <span class="ud-public-table__more">+{value.overflow.length}</span>
                ) : null}
                {card}
            </span>
        );
    }

    if (value.kind === 'fingerprint') {
        return (
            <span class="ud-public-table__cell is-fingerprint" title={value.title}>
                <span class="ud-public-table__ellipsis">{value.value}</span>
                {value.warn ? (
                    <Icon
                        name="alert"
                        size={13}
                        strokeWidth={2.2}
                        className="ud-public-table__warn"
                        aria-label="High spelling risk"
                    />
                ) : null}
                {card}
            </span>
        );
    }

    if (value.kind === 'timing') {
        return (
            <span class={`ud-public-table__cell is-timing is-${value.tone}`} title={value.title}>
                {value.value}
                {card}
            </span>
        );
    }

    if (value.kind === 'upside') {
        return (
            <span class={`ud-public-table__cell is-upside is-${value.tone}`} title={value.title}>
                {value.markers ? (
                    <span class="ud-public-table__upside-track" aria-hidden="true">
                        <span class="ud-public-table__upside-baseline" />
                        {value.markers.medianPercent === null ? null : (
                            <span
                                class="ud-public-table__upside-marker is-median"
                                style={{ left: `${value.markers.medianPercent}%` }}
                            />
                        )}
                        <span
                            class="ud-public-table__upside-marker is-estimate"
                            style={{ left: `${value.markers.estimatePercent}%` }}
                        />
                        <span
                            class="ud-public-table__upside-marker is-price"
                            style={{ left: `${value.markers.pricePercent}%` }}
                        />
                    </span>
                ) : null}
                <span class="ud-public-table__upside-value">{value.value}</span>
                {card}
            </span>
        );
    }

    if (value.kind === 'sales') {
        return (
            <span class="ud-public-table__cell is-sales" title={value.title}>
                <span class="ud-public-table__ellipsis">{value.value}</span>
                <span class="ud-public-table__tier">· {value.tierLabel}</span>
                {card}
            </span>
        );
    }

    return (
        <span class={`ud-public-table__cell is-text is-${value.tone}`} title={value.title}>
            {value.value}
            {card}
        </span>
    );
};

export default PublicDomainsTableCell;
